import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Autocomplete,
} from "@mui/material";
import { format } from "date-fns";
import { IWORequestAction } from "../actions/IWORequestAction";
import HeaderBannerPage from "../components/HeaderBannerPage";
import { getCDLEngList, getCDLSupList } from "../service/IWORequestService";

const fieldStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#f8f9fc",
    "&:hover fieldset": { borderColor: "#142c54" },
    "&.Mui-focused fieldset": { borderColor: "#142c54" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#142c54" },
};

const emptyForm = {
  JobScope: "",
  StartDate: "",
  EndDate: "",
  EstManHrs: "",
  CDL_Supervisior: "",
  CDL_Engineer: "",
  DTS_SupLocation: "",
};

const previewFields = [
  { label: "Job Scope", key: "JobScope" },
  { label: "Start Date", key: "StartDate" },
  { label: "End Date", key: "EndDate" },
  { label: "Estimated Man Hours", key: "EstManHrs" },
  { label: "CDL Supervisor", key: "CDL_Supervisior" },
  { label: "CDL Engineer", key: "CDL_Engineer" },
];

const IWORequest = () => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector((state) => state.iwoRequest);

  const employeeId = localStorage.getItem("employeeId") || "";
  const location = localStorage.getItem("role") || "";

  const [formData, setFormData] = useState(emptyForm);
  const [validationError, setValidationError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [cdlEngList, setCdlEngList] = useState([]);
  const [cdlSupList, setCdlSupList] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const [engRes, supRes] = await Promise.all([
          getCDLEngList(),
          getCDLSupList(),
        ]);
        setCdlEngList(engRes.data || []);
        setCdlSupList(supRes.data || []);
      } catch (err) {
        console.error("Failed to load CDL dropdown lists:", err);
      }
    };
    fetchDropdowns();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReviewClick = () => {
    if (
      !formData.JobScope ||
      !formData.StartDate ||
      !formData.EndDate ||
      !formData.EstManHrs
    ) {
      setValidationError("Please fill in all required fields.");
      return;
    }
    setValidationError("");
    setPreviewOpen(true);
  };

  const handleEdit = () => setPreviewOpen(false);

  const handleConfirmSubmit = () => {
    const now = format(new Date(), "yyyy-MM-dd HH:mm:ss");
    const postData = {
      Id: 0,
      Date: now,
      DTS_Supervisor: employeeId,
      JobScope: formData.JobScope,
      StartDate: formData.StartDate,
      EndDate: formData.EndDate,
      EstManHrs: parseFloat(formData.EstManHrs),
      CDL_Cat: "",
      CDL_Main: "",
      CDL_Supervisior: formData.CDL_Supervisior,
      CDL_Engineer: formData.CDL_Engineer,
      IWO_Status: "R",
      CreatedBy: employeeId,
      CreatedDate: now,
      DTS_SupLocation: location,
    };
    dispatch(IWORequestAction(postData));
    setPreviewOpen(false);
    setIsSubmitAttempted(true);
  };

  useEffect(() => {
    if (isSubmitAttempted && loading === false) {
      if (error) {
        setSnackbarMessage(`Error: ${error}`);
        setSnackbarSeverity("error");
      } else if (data?.responceType === false) {
        setSnackbarMessage(data.responceMessage || "Failed to submit IWO Request.");
        setSnackbarSeverity("error");
      } else if (data?.responceType === true) {
        setSnackbarMessage(data.responceMessage || "IWO Request submitted successfully!");
        setSnackbarSeverity("success");
        setFormData(emptyForm);
        setSelectedEngineer(null);
        setSelectedSupervisor(null);
      }
      setSnackbarOpen(true);
      setIsSubmitAttempted(false);
    }
  }, [loading, error, data, isSubmitAttempted]);

  return (
    <Box sx={{ padding: 0, maxWidth: "100%", margin: "0 auto" }}>
      <HeaderBannerPage />

      <Box sx={{ maxWidth: 500, margin: "0 auto", px: 2, mt: -2, mb: 4 }}>
        {/* Title */}
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontWeight: 700,
            color: "#142c54",
            mb: 2.5,
            letterSpacing: 0.5,
            fontSize: "1.1rem",
          }}
        >
          New IWO Request
        </Typography>

        {validationError && (
          <Alert
            severity="warning"
            sx={{ mb: 2, borderRadius: "10px", fontSize: "0.82rem" }}
          >
            {validationError}
          </Alert>
        )}

        {/* Form Card */}
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e3e8f0",
            borderRadius: "16px",
            p: 2.5,
            backgroundColor: "#fff",
          }}
        >
          <Grid container spacing={2}>
            {/* Job Scope — full width */}
            <Grid item xs={12}>
              <TextField
                label="Job Scope"
                name="JobScope"
                value={formData.JobScope}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                required
                size="small"
                sx={fieldStyle}
              />
            </Grid>

            {/* Start Date / End Date */}
            <Grid item xs={6}>
              <TextField
                label="Start Date"
                name="StartDate"
                type="date"
                value={formData.StartDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
                size="small"
                sx={fieldStyle}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Date"
                name="EndDate"
                type="date"
                value={formData.EndDate}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
                size="small"
                sx={fieldStyle}
              />
            </Grid>

            {/* Est. Man Hours — half width */}
            <Grid item xs={6}>
              <TextField
                label="Est. Man Hours"
                name="EstManHrs"
                type="number"
                inputProps={{ step: "0.5", min: "0" }}
                value={formData.EstManHrs}
                onChange={handleChange}
                fullWidth
                required
                size="small"
                sx={fieldStyle}
              />
            </Grid>

            {/* Spacer so Est. Man Hours stays on left half */}
            <Grid item xs={6} />

            {/* CDL Supervisor */}
            <Grid item xs={12}>
              <Autocomplete
                options={cdlSupList}
                getOptionLabel={(option) => option.EmployeeName || ""}
                isOptionEqualToValue={(option, value) => option.ServiceNo === value.ServiceNo}
                value={selectedSupervisor}
                onChange={(e, newValue) => {
                  setSelectedSupervisor(newValue);
                  setFormData((prev) => ({
                    ...prev,
                    CDL_Supervisior: newValue ? newValue.ServiceNo : "",
                  }));
                }}
                renderInput={(params) => (
                  <TextField {...params} label="CDL Supervisor" size="small" sx={fieldStyle} />
                )}
              />
            </Grid>

            {/* CDL Engineer */}
          <Grid item xs={12}>
            <Autocomplete
              options={cdlEngList}
              getOptionLabel={(option) => option.EmployeeName || ""}
              isOptionEqualToValue={(option, value) => option.ServiceNo === value.ServiceNo}
              value={selectedEngineer}
              onChange={(e, newValue) => {
                setSelectedEngineer(newValue);
                setFormData((prev) => ({
                  ...prev,
                  CDL_Engineer: newValue ? newValue.ServiceNo : "",
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="CDL Engineer" size="small" sx={fieldStyle} />
              )}
            />
          </Grid>
          </Grid>
        </Paper>

        {/* Submit Button */}
        <Box textAlign="center" sx={{ mt: 3 }}>
          <Button
            onClick={handleReviewClick}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#142c54",
              borderRadius: "25px",
              px: 5,
              py: 1.2,
              fontWeight: 600,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              fontSize: "0.85rem",
              boxShadow: "0 4px 14px rgba(20,44,84,0.35)",
              "&:hover": {
                backgroundColor: "#0f2241",
                boxShadow: "0 6px 18px rgba(20,44,84,0.45)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : (
              "Submit Request"
            )}
          </Button>
        </Box>
      </Box>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handleEdit}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
            m: { xs: 2, sm: 4 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#142c54",
            fontSize: "1rem",
            pb: 0.5,
          }}
        >
          Review IWO Request
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          {previewFields.map((field, index) => {
          let displayValue = formData[field.key];
          if (field.key === "CDL_Supervisior") displayValue = selectedSupervisor?.EmployeeName || "—";
          if (field.key === "CDL_Engineer") displayValue = selectedEngineer?.EmployeeName || "—";

          return (
            <Box key={field.key}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, py: 1 }}>
                <Typography sx={{ color: "#777", fontWeight: 600, fontSize: "0.82rem", minWidth: "45%" }}>
                  {field.label}
                </Typography>
                <Typography sx={{ color: "#142c54", textAlign: "right", wordBreak: "break-word", fontSize: "0.85rem", fontWeight: 500 }}>
                  {displayValue || "—"}
                </Typography>
              </Box>
              {index < previewFields.length - 1 && <Divider sx={{ borderColor: "#f0f0f0" }} />}
            </Box>
          );
        })}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
          <Button
            onClick={handleEdit}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              borderColor: "#ddd",
              color: "#555",
              px: 2.5,
            }}
          >
            Edit
          </Button>
          <Button
            onClick={handleConfirmSubmit}
            variant="contained"
            disabled={loading}
            size="small"
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              backgroundColor: "#142c54",
              px: 2.5,
              fontWeight: 600,
              "&:hover": { backgroundColor: "#0f2241" },
            }}
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : (
              "Confirm & Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3500}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%", borderRadius: "10px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IWORequest;